import { z } from 'zod';

/**
 * @typedef {z.infer<typeof productSchema>} ProductSchema
 * @typedef {z.infer<typeof productUpdateSchema>} ProductUpdateSchema
 * @typedef {z.infer<typeof productFilterSchema>} ProductFilterSchema
 */

export const productImageSchema = z.object({
  id: z.string(),
  url: z.string().url('Invalid image URL'),
  isPrimary: z.boolean(),
  sortOrder: z.number().int().min(0)
});

export const productVariantSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Name is required'),
  skuSuffix: z.string().min(1, 'SKU suffix is required'),
  priceOverride: z.number().optional(),
  stock: z.number().int().min(0, 'Stock must be 0 or greater')
});

export const reCommerceAttributesSchema = z.object({
  durabilityRating: z.number().int().min(1).max(10, 'Durability rating must be between 1 and 10'),
  eligibleForResale: z.boolean(),
  eligibleForRepair: z.boolean(),
  eligibleForRental: z.boolean(),
  isRefillable: z.boolean(),
  repairDifficulty: z.enum(['easy', 'medium', 'hard']),
  maxRentalCycles: z.number().int().min(0).optional()
});

export const productSchema = z.object({
  id: z.string(),
  sku: z.string().min(1, 'SKU is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string(),
  category: z.string().min(1, 'Category is required'),
  status: z.enum(['active', 'discontinued', 'draft']),
  stock: z.number().int().min(0, 'Stock must be 0 or greater'),
  price: z.number().min(0, 'Price must be 0 or greater'),
  images: z.array(productImageSchema),
  tags: z.array(z.string()),
  reCommerceAttributes: reCommerceAttributesSchema,
  variants: z.array(productVariantSchema).optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export const productFilterSchema = z.object({
  search: z.string().optional(),
  category: z.string().optional(),
  status: z.enum(['active', 'discontinued', 'draft']).optional(),
  reCommerceFlags: z.object({
    eligibleForResale: z.boolean().optional(),
    eligibleForRepair: z.boolean().optional(),
    eligibleForRental: z.boolean().optional(),
    isRefillable: z.boolean().optional()
  }).optional()
});

export const productUpdateSchema = productSchema.partial().omit({
  id: true,
  createdAt: true,
  updatedAt: true
}); 